import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { finalize } from 'rxjs';
import { UserRole } from '../../../data/UpuServiceDataCertificationRequestModel';
import { ActionButton } from '../../../models';
import { SupportedLang } from '../../../providers/transloco-provider';
import { TranslationDirective } from '../../../shared/translation-directive';
import { UserInitialsPipe } from '../../../shared/user-initials-pipe';
import { ToastService } from '../../../toast-service';
import { TranslationService } from '../../../translation-service';
import { UserApi } from '../../user/user-api';

@Component({
  selector: 'upu-user-menu',
  imports: [
    MatIcon,
    MatButtonModule,
    MatMenuModule,
    TranslationDirective,
    MatDivider,
    RouterLink,
    RouterLinkActive,
    UserInitialsPipe,
  ],
  host: {
    class: 'block',
  },
  templateUrl: './user-menu.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserMenuComponent {
  private readonly toast = inject(ToastService);
  private readonly userApi = inject(UserApi);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translation = inject(TranslationService);

  protected readonly activeLang = signal(this.translation.getActiveLang());
  protected readonly currentUser = this.userApi.user;
  protected readonly userRole = this.userApi.userRole;
  protected readonly updatingLang = signal(false);

  protected readonly supportedLanguages: ActionButton<null, SupportedLang>[] = [
    { id: 'en', value: 'en', label: 'languages.english' },
    { id: 'es', value: 'es', label: 'languages.spanish' },
    { id: 'fr', value: 'fr', label: 'languages.french' },
    // { id: 'pt', value: 'pt', label: 'languages.portuguese' },
    { id: 'ru', value: 'ru', label: 'languages.russian' },
    { id: 'ar', value: 'ar', label: 'languages.arabic' },
  ];

  protected readonly menuOptions = computed<
    ActionButton<null, SupportedLang>[]
  >(() => [
    {
      id: 'language',
      icon: 'translate',
      label: 'common.languages',
      children: this.supportedLanguages,
      disabled: this.updatingLang(),
    },
    {
      id: 'system-settings',
      icon: 'settings',
      label: 'settings.title',
      link: 'settings',
      separator: true,
      visible:
        this.userApi.userRole() === UserRole.Superuser ||
        this.userApi.userRole() === UserRole.Administrator,
    },
  ]);

  protected switchLanguage(lang: SupportedLang | undefined): void {
    if (!lang || lang === this.activeLang()) {
      return;
    }

    this.updatingLang.set(true);

    this.userApi
      .setPreferredLanguage(lang)
      .pipe(
        finalize(() => this.updatingLang.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.translation.setActiveLang(lang);
          this.document.location.reload();
        },
        error: () => {
          this.toast.show({
            type: 'error',
            title: 'languages.updateError.title',
            message: 'languages.updateError.message',
          });
        },
      });
  }
}
